import { Injectable, inject } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { Router, Scroll } from '@angular/router';
import { filter } from 'rxjs/operators';

/** Au-delà, la page n'arrivera plus à cette hauteur : on laisse la personne où elle est. */
const RESTORE_TIMEOUT_MS = 1500;

/**
 * Au retour (bouton ou geste « précédent »), la page reprend là où on l'avait
 * laissée, comme dans une app : revenir d'une fiche plante ne remet plus le
 * potager tout en haut.
 *
 * Le routeur restaure la position une seule fois, juste après la navigation ;
 * or nos listes arrivent après, par l'API, et la page est encore trop courte
 * pour y descendre. On réessaie à chaque image jusqu'à l'atteindre, au plus
 * 1,5 s, et on s'arrête dès que la personne touche l'écran.
 */
@Injectable({ providedIn: 'root' })
export class ScrollRestorationService {
  private readonly router = inject(Router);
  private readonly scroller = inject(ViewportScroller);
  private frame: number | null = null;
  private stopOnUser: (() => void) | null = null;

  listen(): void {
    this.router.events
      .pipe(filter((event): event is Scroll => event instanceof Scroll))
      .subscribe((event) => {
        this.cancel();
        if (event.position) this.restore(event.position);
      });
  }

  private restore([x, y]: [number, number]): void {
    const deadline = Date.now() + RESTORE_TIMEOUT_MS;
    this.stopOnUser = () => this.cancel();
    window.addEventListener('touchstart', this.stopOnUser, { passive: true });
    window.addEventListener('wheel', this.stopOnUser, { passive: true });
    const step = () => {
      this.scroller.scrollToPosition([x, y]);
      if (Math.abs(window.scrollY - y) <= 1 || Date.now() > deadline) {
        this.cancel();
        return;
      }
      this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }

  private cancel(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    if (this.stopOnUser) {
      window.removeEventListener('touchstart', this.stopOnUser);
      window.removeEventListener('wheel', this.stopOnUser);
      this.stopOnUser = null;
    }
  }
}
